import { useState, useRef, useCallback } from "react";

// ═══════════════════════════════════════════════════════════════════════════════
// SEED DATA (self-contained — no external store dependency)
// ═══════════════════════════════════════════════════════════════════════════════
function svg64(emoji, bg, s = 200) {
  const raw = `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}"><rect width="${s}" height="${s}" rx="${s * 0.1}" fill="${bg}"/><text x="${s / 2}" y="${s * 0.65}" font-size="${s * 0.48}" text-anchor="middle" font-family="serif">${emoji}</text></svg>`;
return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(raw)}`;
}

const IMAGES = [
  { id: "i1",  emoji: "🐘", bg: "#E8E4DF", cat: "animals",   en: "Elephant", ml: "ആന",       hi: "हाथी",  ta: "யானை",       te: "ఏనుగు"   },
  { id: "i2",  emoji: "🦁", bg: "#F5E8CC", cat: "animals",   en: "Lion",     ml: "സിംഹം",   hi: "शेर",   ta: "சிங்கம்",   te: "సింహం"   },
  { id: "i3",  emoji: "🐬", bg: "#CCE5F5", cat: "animals",   en: "Dolphin",  ml: "ഡോൾഫിൻ", hi: "डॉल्फिन",ta: "டால்பின்",  te: "డాల్ఫిన్"},
  { id: "i4",  emoji: "🦋", bg: "#F5CCE8", cat: "animals",   en: "Butterfly",ml: "ശലഭം",    hi: "तितली", ta: "பட்டாம்பூச்சி",te:"సీతాకోకచిలుక"},
  { id: "i5",  emoji: "🍎", bg: "#FFD6D6", cat: "fruits",    en: "Apple",    ml: "ആപ്പിൾ", hi: "सेब",   ta: "ஆப்பிள்",   te: "యాపిల్"  },
  { id: "i6",  emoji: "🍌", bg: "#FFFACC", cat: "fruits",    en: "Banana",   ml: "വാഴപ്പഴം",hi: "केला",  ta: "வாழைப்பழம்",te: "అరటిపండు"},
  { id: "i7",  emoji: "🥕", bg: "#FFE0C0", cat: "vegetables",en: "Carrot",   ml: "കാരറ്റ്", hi: "गाजर",  ta: "கேரட்",      te: "క్యారెట్"},
  { id: "i8",  emoji: "🚌", bg: "#FFE5C0", cat: "transport", en: "Bus",      ml: "ബസ്",    hi: "बस",    ta: "பேருந்து",   te: "బస్సు"   },
  { id: "i9",  emoji: "✈️", bg: "#D6E8FF", cat: "transport", en: "Airplane", ml: "വിമാനം", hi: "हवाई जहाज़",ta: "விமானம்",te: "విమానం"  },
  { id: "i10", emoji: "🌳", bg: "#D6F5D6", cat: "nature",    en: "Tree",     ml: "മരം",     hi: "पेड़",   ta: "மரம்",        te: "చెట్టు"  },
  { id: "i11", emoji: "☀️", bg: "#FFF5C0", cat: "nature",    en: "Sun",      ml: "സൂര്യൻ", hi: "सूरज",  ta: "சூரியன்",   te: "సూర్యుడు"},
  { id: "i12", emoji: "🏠", bg: "#E0E8FF", cat: "household", en: "House",    ml: "വീട്",   hi: "घर",    ta: "வீடு",        te: "ఇల్లు"   },
].map(i => ({ ...i, url: svg64(i.emoji, i.bg), thumb: svg64(i.emoji, i.bg, 80) }));

const CHILDREN = [
  { id: "c1", name: "Arjun K.",  age: 5, dob: "2019-03-12", initials: "AK", color: "#1A4A6E", disability: ["Hearing Impairment"],       gender: "M", progress: 68 },
  { id: "c2", name: "Priya M.",  age: 6, dob: "2018-08-24", initials: "PM", color: "#C0392B", disability: ["Autism Spectrum Disorder"],  gender: "F", progress: 54 },
  { id: "c3", name: "Rohan S.",  age: 4, dob: "2020-01-15", initials: "RS", color: "#1B6B45", disability: ["Down Syndrome"],             gender: "M", progress: 42 },
  { id: "c4", name: "Meera R.",  age: 7, dob: "2017-11-05", initials: "MR", color: "#6B2D8B", disability: ["Cerebral Palsy"],            gender: "F", progress: 79 },
];

const DOMAINS = [
  { id: "physical",  label: "Physical & Motor",       color: "#1B6B45", indicators: ["Walks without support","Climbs stairs with support","Runs and stops","Holds pencil","Draws shapes","Cuts with scissors","Self-feeds","Washes hands"] },
  { id: "socio",     label: "Socio-Emotional",         color: "#C0392B", indicators: ["Greets familiar people","Plays alongside peers","Takes turns","Expresses feelings","Manages transitions","Separates from caregiver","Follows classroom rules","Shows empathy"] },
  { id: "cognitive", label: "Cognitive",               color: "#6B2D8B", indicators: ["Sorts by colour","Sorts by shape","Matches objects","Completes 4-piece puzzle","Recalls 3-step instructions","Identifies same/different","Groups objects","Solves simple problems"] },
  { id: "language",  label: "Language & Literacy",     color: "#1A4A6E", indicators: ["Points to pictures","Names 10+ objects","Uses 2-word phrases","Follows 2-step directions","Listens to short stories","Recognises own name in print","Handles books correctly","Retells story with prompts"] },
  { id: "numeracy",  label: "Early Numeracy",          color: "#D4AC0D", indicators: ["Counts 1–5","Counts 1–10","Matches quantities","Identifies more/less","Names 3 shapes","Understands positional words","Identifies patterns AB","Counts backwards from 5"] },
];

const LEVELS = ["not-observed", "emerging", "developing", "achieved"];
const LEVEL_COLORS = { "not-observed": "#E0E0E0", "emerging": "#FFD580", "developing": "#74C9A0", "achieved": "#3D9C6E" };
const LEVEL_LABELS = { "not-observed": "Not Observed", "emerging": "Emerging", "developing": "Developing", "achieved": "Achieved" };

// deterministic demo progress
function demoLevel(childProgress, domainIdx, indIdx) {
  const score = (childProgress + domainIdx * 11 + indIdx * 7) % 100;
  if (score < 20) return "not-observed";
  if (score < 45) return "emerging";
  if (score < 75) return "developing";
  return "achieved";
}

const ACTIVITY_LOG = [
  { icon: "🃏", action: "Animal Flashcard set generated",     child: "Arjun K.",  date: "04 Mar 2026" },
  { icon: "📊", action: "Progress assessment updated",        child: "Meera R.",  date: "04 Mar 2026" },
  { icon: "📝", action: "Odd One Out worksheet printed",      child: "Priya M.",  date: "03 Mar 2026" },
  { icon: "📖", action: "Story Book 'Ellie Finds a Friend'",  child: "Rohan S.",  date: "02 Mar 2026" },
  { icon: "📅", action: "Week of 02 Mar plan created",        child: "All",       date: "01 Mar 2026" },
];

const LANGS = [
  { code: "en", name: "English"   },
  { code: "ml", name: "മലയാളം"   },
  { code: "hi", name: "हिंदी"     },
  { code: "ta", name: "தமிழ்"     },
  { code: "te", name: "తెలుగు"    },
];

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORT MODULES CONFIG
// ═══════════════════════════════════════════════════════════════════════════════
const EXPORT_MODULES = [
  {
    id: "cover",
    icon: "📋",
    label: "Cover Page",
    desc: "Child profile summary with photo, disability profile, and date",
    color: "#0D2137",
    required: true,
    orient: "portrait",
  },
  {
    id: "progress",
    icon: "📊",
    label: "Progress Report",
    desc: "NCF-FS 2022 developmental domain assessment with indicator ratings",
    color: "#5D4037",
    orient: "portrait",
  },
  {
    id: "flashcards",
    icon: "🃏",
    label: "Flashcard Set",
    desc: "2×3 grid flashcards from collection — 6 images per A4 page",
    color: "#1B6B45",
    orient: "portrait",
    hasLang: true,
  },
  {
    id: "worksheet",
    icon: "📝",
    label: "Worksheet",
    desc: "Odd One Out worksheet — 4 images per row, one is the odd one",
    color: "#6B2D8B",
    orient: "portrait",
    hasLang: true,
  },
  {
    id: "activity",
    icon: "🖼️",
    label: "Activity Cards",
    desc: "Full-page A4 image cards — one image per page",
    color: "#7D4800",
    orient: "portrait",
    hasLang: true,
  },
  {
    id: "workplan",
    icon: "📅",
    label: "Weekly Work Plan",
    desc: "Mon–Sat activity grid with NCF domain tags",
    color: "#0D5C63",
    orient: "landscape",
  },
  {
    id: "activity_log",
    icon: "📋",
    label: "Activity Log",
    desc: "Recent platform activity history for records",
    color: "#1A4A6E",
    orient: "portrait",
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --navy: #0D2137; --blue: #1A4A6E; --sky: #2E7DB8; --gold: #C8A84B;
  --cream: #FBF7F0; --fog: #F0F4F8; --ink: #1C2B3A; --mist: #8DA3B4;
  --border: #E4EBF2; --red: #C0392B; --green: #1B6B45;
}
body { font-family: 'DM Sans', system-ui, sans-serif; background: var(--cream); color: var(--ink); }

@keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
@keyframes spin { to { transform: rotate(360deg); } }
.fu { animation: fadeUp .4s ease both; }

/* ─── SCREEN UI ─────────────────────────────────────────────────────────────── */
.mod-card {
  background: white; border-radius: 12px; border: 2px solid var(--border);
  padding: 14px 16px; cursor: pointer; transition: all .18s; position: relative;
}
.mod-card:hover { border-color: var(--sky); box-shadow: 0 4px 18px rgba(13,33,55,.09); }
.mod-card.sel { border-color: var(--navy); box-shadow: 0 4px 20px rgba(13,33,55,.13); }
.mod-card.required { opacity: .7; cursor: default; }
.child-card {
  border-radius: 12px; padding: 11px 14px; cursor: pointer;
  border: 2px solid var(--border); background: white; transition: all .17s;
}
.child-card:hover { border-color: var(--sky); }
.child-card.sel { background: #EBF4FB; }
.btn-primary { background: var(--navy); color: white; border: none; border-radius: 10px; padding: 12px 28px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: 'DM Sans',sans-serif; transition: all .17s; }
.btn-primary:hover { background: #1A3650; box-shadow: 0 6px 20px rgba(13,33,55,.2); }
.btn-primary:disabled { opacity: .5; cursor: default; }
.btn-secondary { background: white; color: var(--ink); border: 1.5px solid var(--border); border-radius: 10px; padding: 10px 20px; font-weight: 600; font-size: 13px; cursor: pointer; font-family: 'DM Sans',sans-serif; transition: all .17s; }
.btn-secondary:hover { background: var(--fog); }
::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-thumb { background: #C5D0DC; border-radius: 4px; }

/* ─── PRINT STYLES ──────────────────────────────────────────────────────────── */
@media print {
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { background: white !important; }
  .no-print { display: none !important; }
  .print-section {
    display: block !important;
    page-break-before: always;
    page-break-after: always;
    page-break-inside: avoid;
  }
  .print-section:first-child { page-break-before: avoid; }
  .print-landscape { size: A4 landscape; }
  @page { margin: 12mm; size: A4 portrait; }
  @page landscape { size: A4 landscape; margin: 10mm; }
}
@media screen {
  .print-preview {
    display: block;
    background: white;
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(0,0,0,.12);
    margin-bottom: 16px;
  }
}
`;

// ═══════════════════════════════════════════════════════════════════════════════
// PRINT SECTION COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ─── NISH HEADER ──────────────────────────────────────────────────────────────
function PrintHeader({ title, subtitle, date }) {
  return (
    <div style={{ borderBottom: "3px solid #0D2137", paddingBottom: 14, marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {/* Logos */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ background: "#0D2137", color: "white", fontWeight: 900, fontSize: 16, padding: "4px 10px", borderRadius: 4, letterSpacing: 0.5, fontFamily: "'DM Sans', sans-serif" }}>NISH</div>
          <div style={{ display: "flex", gap: 3 }}>
            <div style={{ background: "#C0392B", color: "white", fontSize: 7, fontWeight: 700, padding: "1px 5px", borderRadius: 2 }}>NCAHT</div>
            <div style={{ background: "#1B6B45", color: "white", fontSize: 7, fontWeight: 700, padding: "1px 5px", borderRadius: 2 }}>ICMR</div>
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, fontWeight: 900, color: "#0D2137", lineHeight: 1.1 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: "#8DA3B4", marginTop: 3 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ textAlign: "right", fontSize: 10, color: "#8DA3B4" }}>
        <div style={{ fontWeight: 700 }}>NISH Trivandrum</div>
        <div>NCF-FS 2022 · NEP 2020</div>
        {date && <div style={{ marginTop: 3, fontWeight: 600, color: "#0D2137" }}>{date}</div>}
      </div>
    </div>
  );
}

function PrintFooter({ pageLabel }) {
  return (
    <div style={{ borderTop: "1px solid #E4EBF2", paddingTop: 8, marginTop: 20, display: "flex", justifyContent: "space-between", fontSize: 9, color: "#8DA3B4" }}>
      <span>© NISH Trivandrum · NCAHT · ICMR — Foundational Stage Learning Platform</span>
      <span>{pageLabel}</span>
    </div>
  );
}

// ─── COVER PAGE ───────────────────────────────────────────────────────────────
function CoverSection({ child, selectedModules, exportDate }) {
  const inclModules = EXPORT_MODULES.filter(m => selectedModules.has(m.id));
  return (
    <div style={{ padding: "32px 40px", minHeight: 740, display: "flex", flexDirection: "column" }}>
      <PrintHeader title="Child Report Package" subtitle="Foundational Stage Learning Platform" date={exportDate}/>

      {/* Child hero */}
      <div style={{ background: `linear-gradient(135deg, ${child.color}18, ${child.color}05)`, border: `2px solid ${child.color}30`, borderRadius: 16, padding: "28px 32px", marginBottom: 28, display: "flex", alignItems: "center", gap: 28 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", background: child.color, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 900, fontSize: 28, flexShrink: 0, border: `4px solid ${child.color}60` }}>
          {child.initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 900, color: "#0D2137", marginBottom: 5 }}>{child.name}</div>
          <div style={{ display: "flex", gap: 20, fontSize: 13, color: "#5A7A94", marginBottom: 10 }}>
            <span><strong>Age:</strong> {child.age} years</span>
            <span><strong>DOB:</strong> {child.dob}</span>
            <span><strong>Gender:</strong> {child.gender === "M" ? "Male" : "Female"}</span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {child.disability.map(d => (
              <span key={d} style={{ background: child.color, color: "white", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20 }}>{d}</span>
            ))}
          </div>
        </div>
        <div style={{ textAlign: "center", background: child.color, color: "white", borderRadius: 14, padding: "16px 22px", flexShrink: 0 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 900, lineHeight: 1 }}>{child.progress}%</div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 4 }}>Overall Progress</div>
        </div>
      </div>

      {/* NCF progress bars */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#0D2137", marginBottom: 14 }}>NCF-FS 2022 Domain Overview</div>
        {DOMAINS.map((d, di) => {
          const pct = Math.min(100, Math.max(10, child.progress + (di - 2) * 10));
          return (
            <div key={d.id} style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 180, fontSize: 12, fontWeight: 600, color: "#1C2B3A", flexShrink: 0 }}>{d.label}</div>
              <div style={{ flex: 1, height: 12, background: "#F0F4F8", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg, ${d.color}cc, ${d.color})`, borderRadius: 8 }}/>
              </div>
              <div style={{ width: 40, textAlign: "right", fontWeight: 800, fontSize: 13, color: d.color }}>{pct}%</div>
            </div>
          );
        })}
      </div>

      {/* Contents */}
      <div style={{ marginTop: "auto" }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: "#8DA3B4", marginBottom: 10, letterSpacing: 0.5 }}>PACKAGE CONTENTS</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {inclModules.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#F8FAFC", borderRadius: 8, border: "1px solid #E4EBF2" }}>
              <span style={{ fontSize: 16 }}>{m.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#1C2B3A" }}>{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      <PrintFooter pageLabel="Page 1 · Cover"/>
    </div>
  );
}

// ─── PROGRESS REPORT ─────────────────────────────────────────────────────────
function ProgressSection({ child, exportDate }) {
  return (
    <div style={{ padding: "26px 36px", minHeight: 740 }}>
      <PrintHeader title="Progress Tracker Report" subtitle={`${child.name} · NCF-FS 2022 Developmental Assessment`} date={exportDate}/>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {DOMAINS.map((domain, di) => (
          <div key={domain.id} style={{ border: `2px solid ${domain.color}30`, borderRadius: 10, overflow: "hidden", marginBottom: 4 }}>
            <div style={{ background: domain.color, color: "white", padding: "8px 14px", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span>{domain.label}</span>
            </div>
            <div style={{ padding: "10px 14px" }}>
              {domain.indicators.map((ind, ii) => {
                const level = demoLevel(child.progress, di, ii);
                return (
                  <div key={ii} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0", borderBottom: ii < domain.indicators.length - 1 ? "1px solid #F0F4F8" : "none" }}>
                    <span style={{ fontSize: 11, color: "#1C2B3A", flex: 1 }}>{ind}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: LEVEL_COLORS[level], color: level === "not-observed" ? "#888" : "#1C2B3A", flexShrink: 0, marginLeft: 8 }}>
                      {LEVEL_LABELS[level]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ marginTop: 14, display: "flex", gap: 16, alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#8DA3B4" }}>LEVEL KEY:</span>
        {LEVELS.map(l => (
          <span key={l} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: LEVEL_COLORS[l], display: "inline-block" }}/>
            {LEVEL_LABELS[l]}
          </span>
        ))}
      </div>

      <PrintFooter pageLabel="Progress Report"/>
    </div>
  );
}

// ─── FLASHCARD SET ────────────────────────────────────────────────────────────
function FlashcardSection({ child, lang, exportDate }) {
  const pages = [];
  for (let i = 0; i < IMAGES.length; i += 6) pages.push(IMAGES.slice(i, i + 6));
  return (
    <>
      {pages.map((pageImgs, pi) => (
        <div key={pi} style={{ padding: "26px 32px", minHeight: 720 }}>
          {pi === 0 && <PrintHeader title="Flashcard Set" subtitle={`${child.name} · Language: ${LANGS.find(l => l.code === lang)?.name || "English"}`} date={exportDate}/>}
          {pi > 0 && <div style={{ marginBottom: 16, fontSize: 11, color: "#8DA3B4", fontWeight: 600 }}>Flashcard Set — Page {pi + 1}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            {pageImgs.map(img => (
              <div key={img.id} style={{ border: "2px solid #E4EBF2", borderRadius: 12, overflow: "hidden", textAlign: "center" }}>
                <div style={{ padding: 12, background: "#F8FAFC" }}>
                  <img src={img.url} style={{ width: 120, height: 120, objectFit: "contain" }} alt={img[lang] || img.en}/>
                </div>
                <div style={{ padding: "10px 8px", background: "white" }}>
                  <div style={{ fontWeight: 800, fontSize: 16, color: "#0D2137", marginBottom: 3 }}>{img[lang] || img.en}</div>
                  {lang !== "en" && <div style={{ fontSize: 12, color: "#8DA3B4" }}>{img.en}</div>}
                  <div style={{ marginTop: 6, fontSize: 9, color: "#C0D0DF", fontWeight: 600, textTransform: "uppercase" }}>{img.cat}</div>
                </div>
              </div>
            ))}
          </div>
          <PrintFooter pageLabel={`Flashcards · Page ${pi + 1}`}/>
        </div>
      ))}
    </>
  );
}

// ─── WORKSHEET ────────────────────────────────────────────────────────────────
function WorksheetSection({ child, lang, exportDate }) {
  const rows = [
    IMAGES.slice(0, 4),
    IMAGES.slice(4, 8),
    IMAGES.slice(8, 12),
  ];
  const oddIndexes = [2, 1, 3]; // which index is the "odd" one per row

  return (
    <div style={{ padding: "26px 36px", minHeight: 740 }}>
      <PrintHeader title="Odd One Out Worksheet" subtitle={`${child.name} · Language: ${LANGS.find(l => l.code === lang)?.name || "English"}`} date={exportDate}/>

      <div style={{ fontSize: 12, color: "#5A7A94", marginBottom: 20, lineHeight: 1.6, padding: "10px 14px", background: "#F8FAFC", borderRadius: 8, border: "1px solid #E4EBF2" }}>
        <strong>Instructions:</strong> Look at each row of pictures. Circle or point to the one that does not belong with the others. There are no wrong answers — follow the child's thinking!
      </div>

      {rows.map((row, ri) => (
        <div key={ri} style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#8DA3B4", marginBottom: 10 }}>Row {ri + 1}</div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {row.map((img, ii) => (
              <div key={img.id} style={{ flex: 1, border: `2px solid ${ii === oddIndexes[ri] ? "#E4EBF2" : "#E4EBF2"}`, borderRadius: 10, overflow: "hidden", textAlign: "center" }}>
                <div style={{ padding: 10, background: "#F8FAFC" }}>
                  <img src={img.url} style={{ width: 80, height: 80, objectFit: "contain" }} alt={img[lang] || img.en}/>
                </div>
                <div style={{ padding: "7px 6px", fontSize: 12, fontWeight: 700, color: "#0D2137" }}>{img[lang] || img.en}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: "#8DA3B4" }}>Teacher's note:</span>
            <div style={{ flex: 1, borderBottom: "1px solid #E4EBF2", height: 20 }}/>
          </div>
        </div>
      ))}

      <div style={{ marginTop: 10, padding: "10px 14px", background: "#FFF3CC", borderRadius: 8, border: "1px solid #E4D090" }}>
        <div style={{ fontSize: 10, color: "#7D6520" }}>⚡ <strong>Teacher note:</strong> This is an open-ended thinking exercise, not a test. Celebrate the child's reasoning, not just the "correct" answer.</div>
      </div>

      <PrintFooter pageLabel="Worksheet · Odd One Out"/>
    </div>
  );
}

// ─── ACTIVITY CARDS ───────────────────────────────────────────────────────────
function ActivityCardsSection({ child, lang, exportDate }) {
  // 4 full-page cards (one per page-break div)
  const cards = IMAGES.slice(0, 4);
  return (
    <>
      {cards.map((img, i) => (
        <div key={img.id} style={{ padding: "32px 40px", minHeight: 720, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
          {i === 0 && <div style={{ position: "absolute", top: 26, left: 40, right: 40 }}><PrintHeader title="Activity Cards" subtitle={`${child.name}`} date={exportDate}/></div>}
          <div style={{ background: "#F8FAFC", borderRadius: 20, padding: 40, border: "3px solid #E4EBF2", marginTop: i === 0 ? 60 : 0, width: "100%", maxWidth: 520 }}>
            <img src={img.url} style={{ width: 280, height: 280, objectFit: "contain" }} alt={img[lang] || img.en}/>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 900, color: "#0D2137", marginTop: 20 }}>{img[lang] || img.en}</div>
            {lang !== "en" && <div style={{ fontSize: 20, color: "#8DA3B4", marginTop: 8 }}>{img.en}</div>}
            <div style={{ marginTop: 14, fontSize: 13, color: "#8DA3B4", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>{img.cat}</div>
          </div>
          <PrintFooter pageLabel={`Activity Card ${i + 1} of ${cards.length}`}/>
        </div>
      ))}
    </>
  );
}

// ─── WORK PLAN ────────────────────────────────────────────────────────────────
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SAMPLE_ACTIVITIES = {
  Monday:    [{ type: "flashcard", title: "Animal Flashcards", dur: "20 min", domain: "Language & Literacy" }, { type: "worksheet", title: "Odd One Out", dur: "15 min", domain: "Cognitive" }],
  Tuesday:   [{ type: "art", title: "Free Drawing", dur: "25 min", domain: "Physical & Motor" }],
  Wednesday: [{ type: "storybook", title: "Ellie Finds a Friend", dur: "30 min", domain: "Socio-Emotional" }, { type: "music", title: "Animal Songs", dur: "15 min", domain: "Language & Literacy" }],
  Thursday:  [{ type: "play", title: "Sorting Game", dur: "20 min", domain: "Cognitive" }, { type: "art", title: "Handprint Art", dur: "20 min", domain: "Physical & Motor" }],
  Friday:    [{ type: "assessment", title: "Week Review", dur: "20 min", domain: "Cognitive" }],
  Saturday:  [],
};
const DOMAIN_COLORS_MAP = { "Language & Literacy": "#1A4A6E", "Cognitive": "#6B2D8B", "Physical & Motor": "#1B6B45", "Socio-Emotional": "#C0392B", "Early Numeracy": "#D4AC0D" };

function WorkPlanSection({ child, exportDate }) {
  return (
    <div style={{ padding: "20px 28px", minHeight: 540 }}>
      <PrintHeader title="Weekly Work Plan" subtitle={`${child.name} · Week of 02 Mar 2026 · Theme: Animals`} date={exportDate}/>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 12 }}>
        {DAYS.map(day => (
          <div key={day}>
            <div style={{ background: "#0D2137", color: "white", padding: "6px 8px", borderRadius: "6px 6px 0 0", fontSize: 11, fontWeight: 700, textAlign: "center" }}>{day.slice(0, 3)}</div>
            <div style={{ border: "1px solid #E4EBF2", borderTop: "none", borderRadius: "0 0 6px 6px", minHeight: 160, padding: 6, background: "white" }}>
              {(SAMPLE_ACTIVITIES[day] || []).map((act, i) => {
                const col = DOMAIN_COLORS_MAP[act.domain] || "#666";
                return (
                  <div key={i} style={{ background: `${col}12`, border: `1px solid ${col}30`, borderRadius: 5, padding: "5px 7px", marginBottom: 5 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: col, marginBottom: 2 }}>{act.title}</div>
                    <div style={{ fontSize: 9, color: "#8DA3B4" }}>{act.dur} · {act.domain.split(" ")[0]}</div>
                  </div>
                );
              })}
              {(SAMPLE_ACTIVITIES[day] || []).length === 0 && (
                <div style={{ fontSize: 9, color: "#C5D0DC", textAlign: "center", paddingTop: 20 }}>Rest day</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {Object.entries(DOMAIN_COLORS_MAP).map(([d, c]) => (
          <span key={d} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block" }}/>
            {d}
          </span>
        ))}
      </div>

      <PrintFooter pageLabel="Weekly Work Plan"/>
    </div>
  );
}

// ─── ACTIVITY LOG ────────────────────────────────────────────────────────────
function ActivityLogSection({ child, exportDate }) {
  const filtered = ACTIVITY_LOG.filter(a => a.child === child.name || a.child === "All");
  const all = filtered.length > 0 ? filtered : ACTIVITY_LOG;
  return (
    <div style={{ padding: "26px 36px", minHeight: 740 }}>
      <PrintHeader title="Activity Log" subtitle={`${child.name} · Platform activity history`} date={exportDate}/>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#0D2137", color: "white" }}>
            {["Date", "Module", "Activity", "Child"].map(h => (
              <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: 11 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ACTIVITY_LOG.map((a, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "white" : "#F8FAFC" }}>
              <td style={{ padding: "9px 12px", color: "#5A7A94", fontWeight: 600 }}>{a.date}</td>
              <td style={{ padding: "9px 12px" }}><span style={{ fontSize: 16 }}>{a.icon}</span></td>
              <td style={{ padding: "9px 12px", fontWeight: 600, color: "#1C2B3A" }}>{a.action}</td>
              <td style={{ padding: "9px 12px", color: "#5A7A94" }}>{a.child}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 20, fontSize: 10, color: "#8DA3B4", lineHeight: 1.7 }}>
        This log is generated from the NISH FSLP platform. For complete history, refer to the platform activity feed.
      </div>
      <PrintFooter pageLabel="Activity Log"/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN BATCH EXPORT UI
// ═══════════════════════════════════════════════════════════════════════════════
export default function BatchExport() {
  const [selectedChild, setSelectedChild] = useState(CHILDREN[0].id);
  const [selectedModules, setSelectedModules] = useState(new Set(["cover", "progress", "flashcards"]));
  const [lang, setLang] = useState("en");
  const [step, setStep] = useState("configure"); // configure | preview
  const [printing, setPrinting] = useState(false);
  const previewRef = useRef();

  const child = CHILDREN.find(c => c.id === selectedChild);
  const exportDate = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  const toggleModule = (id) => {
    if (id === "cover") return; // required
    setSelectedModules(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handlePrint = useCallback(() => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 300);
  }, []);

  const orderedSelected = EXPORT_MODULES.filter(m => selectedModules.has(m.id));

  const hasLang = orderedSelected.some(m => m.hasLang);

  return (
    <>
      <style>{STYLE}</style>

      {/* ── SCREEN UI ──────────────────────────────────────────────────────── */}
      <div className="no-print" style={{ minHeight: "100vh", background: "var(--cream)", fontFamily: "'DM Sans', system-ui, sans-serif" }}>

        {/* Top bar */}
        <div style={{ background: "var(--navy)", padding: "0 32px", height: 56, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ background: "white", color: "var(--navy)", fontWeight: 900, fontSize: 13, padding: "3px 8px", borderRadius: 4, letterSpacing: 0.5 }}>NISH</div>
          <div style={{ height: 20, width: 1, background: "rgba(255,255,255,.2)" }}/>
          <div style={{ color: "white", fontWeight: 700, fontSize: 14 }}>Batch PDF Export</div>
          <div style={{ flex: 1 }}/>
          {step === "preview" && (
            <button className="btn-secondary" onClick={() => setStep("configure")} style={{ fontSize: 12, padding: "6px 14px" }}>← Configure</button>
          )}
          <button className="btn-primary" onClick={step === "configure" ? () => setStep("preview") : handlePrint} disabled={selectedModules.size === 0 || printing} style={{ fontSize: 13, padding: "9px 22px" }}>
            {printing ? "Preparing…" : step === "configure" ? "Preview & Export →" : "🖨️ Print / Save PDF"}
          </button>
        </div>

        {step === "configure" ? (
          <div style={{ padding: "32px", maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 900, color: "var(--navy)", marginBottom: 5 }}>Configure Batch Export</div>
            <div style={{ fontSize: 13, color: "var(--mist)", marginBottom: 32 }}>Select a child, choose which modules to include, then preview and print as a single PDF.</div>

            <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 28 }}>

              {/* Left: child + lang */}
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--navy)", marginBottom: 14 }}>1 · Select Child</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
                  {CHILDREN.map(c => (
                    <div key={c.id} className={`child-card${selectedChild === c.id ? " sel" : ""}`} onClick={() => setSelectedChild(c.id)} style={{ borderColor: selectedChild === c.id ? c.color : "var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: c.color, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{c.initials}</div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "var(--ink)" }}>{c.name}</div>
                          <div style={{ fontSize: 10, color: "var(--mist)" }}>Age {c.age} · {c.disability[0]}</div>
                        </div>
                        {selectedChild === c.id && <span style={{ marginLeft: "auto", color: c.color, fontSize: 16 }}>✓</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {hasLang && (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "var(--navy)", marginBottom: 10 }}>2 · Label Language</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {LANGS.map(l => (
                        <button key={l.code} onClick={() => setLang(l.code)} style={{ padding: "8px 14px", border: lang === l.code ? "2px solid var(--navy)" : "1.5px solid var(--border)", borderRadius: 9, background: lang === l.code ? "#EBF4FB" : "white", color: lang === l.code ? "var(--navy)" : "#555", fontWeight: lang === l.code ? 700 : 500, fontSize: 13, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, fontFamily: "'DM Sans', sans-serif" }}>
                          <span style={{ fontSize: 16 }}>🏷️</span>
                          <span>{l.name}</span>
                          {lang === l.code && <span style={{ marginLeft: "auto", color: "var(--sky)" }}>✓</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Right: module selection */}
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--navy)", marginBottom: 14 }}>{hasLang ? "3" : "2"} · Choose Modules <span style={{ fontSize: 11, color: "var(--mist)", fontWeight: 500 }}>({selectedModules.size} selected)</span></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {EXPORT_MODULES.map(m => {
                    const isSel = selectedModules.has(m.id);
                    return (
                      <div key={m.id} className={`mod-card${isSel ? " sel" : ""}${m.required ? " required" : ""}`} onClick={() => toggleModule(m.id)} style={{ borderColor: isSel ? m.color : "var(--border)" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: isSel ? `${m.color}18` : "var(--fog)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{m.icon}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--navy)" }}>{m.label}</span>
                              {m.required && <span style={{ fontSize: 9, fontWeight: 700, background: "#FFF3CC", color: "#7D6520", padding: "1px 6px", borderRadius: 6 }}>Required</span>}
                              {m.orient === "landscape" && <span style={{ fontSize: 9, fontWeight: 700, background: "#E8EAF6", color: "#1A237E", padding: "1px 6px", borderRadius: 6 }}>Landscape</span>}
                              {m.hasLang && <span style={{ fontSize: 9, fontWeight: 700, background: "#E8F8F5", color: "#0D5C63", padding: "1px 6px", borderRadius: 6 }}>Multilingual</span>}
                            </div>
                            <div style={{ fontSize: 11, color: "var(--mist)", lineHeight: 1.5 }}>{m.desc}</div>
                          </div>
                          {/* Checkbox */}
                          <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${isSel ? m.color : "var(--border)"}`, background: isSel ? m.color : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                            {isSel && <span style={{ color: "white", fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary */}
                <div style={{ marginTop: 18, background: "white", border: "1.5px solid var(--border)", borderRadius: 12, padding: "14px 18px" }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "var(--navy)", marginBottom: 10 }}>Export Summary</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {[
                      { label: "Child",    val: child?.name },
                      { label: "Modules",  val: `${selectedModules.size} sections` },
                      { label: "Language", val: LANGS.find(l => l.code === lang)?.name || "English" },
                      { label: "Date",     val: exportDate },
                      { label: "Format",   val: "A4 PDF" },
                      { label: "Images",   val: `${IMAGES.length} in library` },
                    ].map(r => (
                      <div key={r.label} style={{ padding: "8px 10px", background: "var(--fog)", borderRadius: 8 }}>
                        <div style={{ fontSize: 9, color: "var(--mist)", fontWeight: 700, letterSpacing: 0.5 }}>{r.label.toUpperCase()}</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", marginTop: 2 }}>{r.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : (
          /* ── PREVIEW ─────────────────────────────────────────────────────── */
          <div style={{ padding: "24px 32px", maxWidth: 900, margin: "0 auto" }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 900, color: "var(--navy)", marginBottom: 4 }}>Preview — {child.name}</div>
            <div style={{ fontSize: 13, color: "var(--mist)", marginBottom: 22 }}>
              {selectedModules.size} sections · {LANGS.find(l => l.code === lang)?.name} labels · Click "Print / Save PDF" to export
            </div>

            {/* Section previews */}
            {orderedSelected.map((m, i) => (
              <div key={m.id} className={`print-preview fu`} style={{ animationDelay: `${i * 60}ms` }}>
                <div style={{ background: m.color, color: "white", padding: "8px 18px", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15 }}>{m.icon}</span>
                  <span>{m.label}</span>
                  {m.orient === "landscape" && <span style={{ opacity: 0.7, fontWeight: 500 }}>· Landscape</span>}
                  {m.hasLang && <span style={{ opacity: 0.7, fontWeight: 500 }}>· {LANGS.find(l => l.code === lang)?.name}</span>}
                  <span style={{ marginLeft: "auto", opacity: 0.6, fontWeight: 500, fontSize: 10 }}>Section {i + 1}</span>
                </div>
                <div style={{ transform: "scale(0.72)", transformOrigin: "top left", width: "138.9%", pointerEvents: "none" }}>
                  {m.id === "cover"        && <CoverSection        child={child} selectedModules={selectedModules} exportDate={exportDate}/>}
                  {m.id === "progress"     && <ProgressSection     child={child} exportDate={exportDate}/>}
                  {m.id === "flashcards"   && <FlashcardSection    child={child} lang={lang} exportDate={exportDate}/>}
                  {m.id === "worksheet"    && <WorksheetSection    child={child} lang={lang} exportDate={exportDate}/>}
                  {m.id === "activity"     && <ActivityCardsSection child={child} lang={lang} exportDate={exportDate}/>}
                  {m.id === "workplan"     && <WorkPlanSection     child={child} exportDate={exportDate}/>}
                  {m.id === "activity_log" && <ActivityLogSection  child={child} exportDate={exportDate}/>}
                </div>
              </div>
            ))}

            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <button className="btn-primary" onClick={handlePrint} disabled={printing} style={{ fontSize: 15, padding: "14px 36px" }}>
                {printing ? "🔄 Preparing…" : "🖨️ Print / Save as PDF"}
              </button>
              <div style={{ fontSize: 11, color: "var(--mist)", marginTop: 10 }}>In the print dialog, choose "Save as PDF" to get a file. Set margins to "None" for best results.</div>
            </div>
          </div>
        )}
      </div>

      {/* ── PRINT OUTPUT ───────────────────────────────────────────────────── */}
      <div ref={previewRef} style={{ display: "none" }} className="print-only">
        {/* Rendered on screen inside preview, printed via window.print() */}
      </div>

      {/* Actual printable sections — always in DOM, hidden on screen */}
      {selectedModules.has("cover") && (
        <div className="print-section" style={{ display: "none" }}>
          <CoverSection child={child} selectedModules={selectedModules} exportDate={exportDate}/>
        </div>
      )}
      {selectedModules.has("progress") && (
        <div className="print-section" style={{ display: "none" }}>
          <ProgressSection child={child} exportDate={exportDate}/>
        </div>
      )}
      {selectedModules.has("flashcards") && (
        <div className="print-section" style={{ display: "none" }}>
          <FlashcardSection child={child} lang={lang} exportDate={exportDate}/>
        </div>
      )}
      {selectedModules.has("worksheet") && (
        <div className="print-section" style={{ display: "none" }}>
          <WorksheetSection child={child} lang={lang} exportDate={exportDate}/>
        </div>
      )}
      {selectedModules.has("activity") && (
        <div className="print-section" style={{ display: "none" }}>
          <ActivityCardsSection child={child} lang={lang} exportDate={exportDate}/>
        </div>
      )}
      {selectedModules.has("workplan") && (
        <div className="print-section" style={{ display: "none" }}>
          <WorkPlanSection child={child} exportDate={exportDate}/>
        </div>
      )}
      {selectedModules.has("activity_log") && (
        <div className="print-section" style={{ display: "none" }}>
          <ActivityLogSection child={child} exportDate={exportDate}/>
        </div>
      )}
    </>
  );
}
